import { useCallback } from 'react'
import { StringifyRequest } from '@/api/api'
import { useFetchUserStatusQuery } from '@/api/user-api'
import { useLazyFetchInfinitePostCollectionListQuery } from '@/api/workspaces-api'
import { FetchPostCollectionListRequest } from '@/types/api/post-collection'
import { PostCollectionList } from '@/types/schema/api/post-collection-list'

type UsePostCollectionData = () => {
  isFetchingCollections: boolean
  collections?: PostCollectionList
  fetchCollections:
    | ((
        params: Omit<
          FetchPostCollectionListRequest,
          keyof StringifyRequest | 'workspaceId'
        >,
      ) => ReturnType<typeof useLazyFetchInfinitePostCollectionListQuery>[0])
    | ((
        params: Omit<
          FetchPostCollectionListRequest,
          keyof StringifyRequest | 'workspaceId'
        >,
      ) => void)
}

const usePostCollectionData: UsePostCollectionData = () => {
  const { data: userStatus } = useFetchUserStatusQuery()

  const [
    fetchCollectionsFunc,
    { data: collections, isFetching: isFetchingCollections },
  ] = useLazyFetchInfinitePostCollectionListQuery()

  const fetchCollections: ReturnType<UsePostCollectionData>['fetchCollections'] =
    useCallback(
      (
        params: Omit<
          FetchPostCollectionListRequest,
          keyof StringifyRequest | 'workspaceId'
        >,
      ) => {
        if (!userStatus?.currentWorkspaceId) {
          return
        }

        return fetchCollectionsFunc({
          workspaceId: userStatus.currentWorkspaceId,
          stringify: true,
          arrayFormat: 'bracket',
          page: params.page,
          name: params.name,
          collectedPostIds: params.collectedPostIds,
          collectedPlatformType: params.collectedPlatformType,
          perPage: params.perPage,
          reset: params.reset,
        })
      },
      [fetchCollectionsFunc, userStatus?.currentWorkspaceId],
    )

  return {
    isFetchingCollections,
    collections,
    fetchCollections,
  }
}

export default usePostCollectionData
